import { COVERAGE_REPORT_STATUS_LABEL, type CoverageLookups } from './coverageReport'
import type { CoverageFilterState } from './coverageFilters'
import type { ManpowerRow, ManpowerTotals } from './manpowerFigures'
import type { ExportTable } from './reportExport'
import { supervisorLabel } from './supervisorLabel'

export type ManpowerExportInput = {
  from: string
  to: string
  filters: CoverageFilterState
  lookups: CoverageLookups
  view: 'day' | 'shift'
  generatedAt: Date
  // Names the report; the two-week report when omitted.
  title?: string
}

export function manpowerExportTable(rows: ManpowerRow[], totals: ManpowerTotals, input: ManpowerExportInput): ExportTable {
  const { filters, lookups } = input
  const byShift = input.view === 'shift'
  return {
    title: input.title ?? 'Two-Week Manpower Report',
    parameters: [
      { label: 'Date range', value: `${input.from} to ${input.to}` },
      { label: 'View', value: byShift ? 'By shift' : 'By day' },
      { label: 'Team', value: filters.teamId ? (lookups.teams.get(filters.teamId) ?? 'Unknown') : 'All' },
      { label: 'Supervisor', value: filters.supervisorId ? supervisorLabel(filters.supervisorId, lookups) : 'All' },
      { label: 'Shift', value: filters.shiftId ? (lookups.shifts.get(filters.shiftId) ?? 'Unknown') : 'All' },
      { label: 'Status', value: filters.status ? COVERAGE_REPORT_STATUS_LABEL[filters.status] : 'All' },
    ],
    generatedAt: input.generatedAt,
    columns: [
      { header: 'Date', width: 12 },
      ...(byShift ? [{ header: 'Shift' }] : []),
      { header: 'Total manpower', numeric: true },
      { header: 'On duty', numeric: true },
      { header: 'Available', numeric: true },
      { header: 'Available %', numeric: true, percent: true },
      { header: 'On leave', numeric: true },
      { header: 'Detailed out', numeric: true },
      { header: 'Planned overtime (hours)', numeric: true, width: 22 },
      { header: 'Coverage status', width: 28 },
    ],
    rows: rows.map((row) => [
      row.date,
      ...(byShift ? [row.shift] : []),
      row.totalManpower,
      row.onDuty,
      row.available,
      row.availablePercent,
      row.onLeave,
      row.detailedOut,
      row.plannedOvertime,
      COVERAGE_REPORT_STATUS_LABEL[row.status],
    ]),
    totals: {
      label: 'Total',
      values: [
        ...(byShift ? [null, null] : [null]),
        totals.totalManpower,
        totals.onDuty,
        totals.available,
        totals.availablePercent,
        totals.onLeave,
        totals.detailedOut,
        totals.plannedOvertime,
        null,
      ],
      // The percentage is the share of the totals, not a sum of the rows.
      fixedColumns: [byShift ? 5 : 4],
    },
  }
}
